import { useEffect, useState } from 'react'
import { Alert, Button, Form, Spinner } from 'react-bootstrap'
import { Link, useSearchParams } from 'react-router-dom'

import PaginaPublica from '../../components/Auth/PaginaPublica'
import api from '../../services/api'
import {
  FINALIDADES_SECUNDARIAS,
  TIPOS_LEGALES,
  aceptoTodo,
  datosRegistro,
  documentosCambiaron,
  mensajeDeError,
} from '../../utils/registroPublico'

const VACIO = {
  nombre: '', email: '', telefono: '', despacho: '', rfc_titular: '', estado: '', ciudad: '',
  password: '', sitio_web: '',
  acepta_aviso_privacidad: false, acepta_terminos: false, acepta_condiciones_uso: false,
  acepta_promociones: false, acepta_oferta_plan: false, acepta_encuestas: false,
  acepta_novedades: false,
}

/**
 * La página del QR: `/registro?c=CNC2026`.
 *
 * Pregunta primero si la campaña está abierta y trae los documentos legales vigentes; sólo
 * entonces muestra el formulario. Al enviar, el invitado no queda dentro todavía: tiene que
 * confirmar su correo (`/registro/verificar`).
 */
export default function Registro() {
  const [params] = useSearchParams()
  const codigo = (params.get('c') || '').toLowerCase()

  const [campana, setCampana] = useState(null)
  const [legales, setLegales] = useState([])
  const [cargando, setCargando] = useState(true)
  const [form, setForm] = useState(VACIO)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState('')
  const [enviado, setEnviado] = useState('')

  const cargarLegales = () =>
    api.get('/auth/legales/').then((r) => setLegales(r.data))

  useEffect(() => {
    let vivo = true
    if (!codigo) {
      setCargando(false)
      return undefined
    }
    Promise.all([api.get(`/auth/registro/campana/${codigo}/`), api.get('/auth/legales/')])
      .then(([c, l]) => {
        if (!vivo) return
        setCampana(c.data)
        setLegales(l.data)
      })
      .catch((e) => vivo && setError(mensajeDeError(e)))
      .finally(() => vivo && setCargando(false))
    return () => {
      vivo = false
    }
  }, [codigo])

  const cambiar = (campo) => (e) => {
    const valor = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setForm((f) => ({ ...f, [campo]: valor }))
  }

  const enviar = async (e) => {
    e.preventDefault()
    setError('')
    setEnviando(true)
    try {
      const r = await api.post('/auth/registro/', datosRegistro(form, legales, codigo))
      setEnviado(r.data?.detail || 'Revisa tu correo para confirmar tu cuenta.')
    } catch (err) {
      setError(mensajeDeError(err))
      if (documentosCambiaron(err)) {
        // El texto que aceptó ya no es el vigente: se recarga y se le pide aceptarlo de nuevo.
        await cargarLegales()
        setForm((f) => ({
          ...f, acepta_aviso_privacidad: false, acepta_terminos: false, acepta_condiciones_uso: false,
        }))
      }
    } finally {
      setEnviando(false)
    }
  }

  const plan = campana?.plan
  const subtitulo = plan
    ? `${plan.timbres} timbres reales y ${plan.dias} días para facturar con tu propio RFC, sin costo.`
    : 'Facturación CFDI 4.0 para contadores.'

  let cuerpo
  if (cargando) {
    cuerpo = <div className="text-center py-4"><Spinner animation="border" /></div>
  } else if (enviado) {
    cuerpo = (
      <Alert variant="success" className="mb-0" data-testid="registro-enviado">
        <strong>¡Casi listo!</strong> {enviado}
        <div className="small mt-2">
          La liga vence en 48 horas. ¿No llegó? Revisa spam o vuelve a registrarte con el mismo
          correo para recibir una nueva.
        </div>
      </Alert>
    )
  } else if (!codigo || !campana?.abierta) {
    cuerpo = (
      <Alert variant="secondary" className="mb-0">
        {campana?.motivo || error || 'Este registro no está disponible.'}
      </Alert>
    )
  } else {
    cuerpo = (
      <Form onSubmit={enviar} noValidate={false}>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group className="mb-3" controlId="registroNombre">
          <Form.Label>Nombre completo</Form.Label>
          <Form.Control value={form.nombre} onChange={cambiar('nombre')} autoComplete="name"
                        maxLength={150} required />
        </Form.Group>
        <Form.Group className="mb-3" controlId="registroEmail">
          <Form.Label>Correo</Form.Label>
          <Form.Control type="email" value={form.email} onChange={cambiar('email')}
                        autoComplete="email" maxLength={150} required />
          <Form.Text>Será tu usuario para entrar.</Form.Text>
        </Form.Group>
        <Form.Group className="mb-3" controlId="registroTelefono">
          <Form.Label>Teléfono con WhatsApp</Form.Label>
          <Form.Control type="tel" value={form.telefono} onChange={cambiar('telefono')}
                        autoComplete="tel" inputMode="tel" maxLength={20} required />
        </Form.Group>
        <Form.Group className="mb-3" controlId="registroRfc">
          <Form.Label>Tu RFC</Form.Label>
          <Form.Control value={form.rfc_titular} onChange={cambiar('rfc_titular')}
                        style={{ textTransform: 'uppercase' }} maxLength={13}
                        autoComplete="off" required />
          <Form.Text>La prueba gratuita es una por persona.</Form.Text>
        </Form.Group>
        <Form.Group className="mb-3" controlId="registroDespacho">
          <Form.Label>Despacho o empresa</Form.Label>
          <Form.Control value={form.despacho} onChange={cambiar('despacho')}
                        autoComplete="organization" maxLength={150} required />
        </Form.Group>
        <Form.Group className="mb-3" controlId="registroEstado">
          <Form.Label>Estado</Form.Label>
          <Form.Select value={form.estado} onChange={cambiar('estado')} required>
            <option value="">Elige tu estado</option>
            {(campana.entidades || []).map((e) => (
              <option key={e.clave} value={e.clave}>{e.nombre}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3" controlId="registroCiudad">
          <Form.Label>Ciudad o municipio</Form.Label>
          <Form.Control value={form.ciudad} onChange={cambiar('ciudad')}
                        autoComplete="address-level2" maxLength={120} required />
        </Form.Group>
        <Form.Group className="mb-3" controlId="registroPassword">
          <Form.Label>Contraseña</Form.Label>
          <Form.Control type="password" value={form.password} onChange={cambiar('password')}
                        autoComplete="new-password" minLength={8} required />
          <Form.Text>Al menos 8 caracteres, que no sean sólo números.</Form.Text>
        </Form.Group>

        {/* Honeypot: fuera de la vista y del tabulador. Una persona no lo llena; un bot sí. */}
        <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px' }}>
          <label htmlFor="registroSitioWeb">Sitio web</label>
          <input id="registroSitioWeb" tabIndex={-1} autoComplete="off"
                 value={form.sitio_web} onChange={cambiar('sitio_web')} />
        </div>

        {TIPOS_LEGALES.map(({ tipo, campo, etiqueta }) => (
          <Form.Check
            key={campo}
            id={`registro-${campo}`}
            className="mb-2"
            checked={form[campo]}
            onChange={cambiar(campo)}
            label={
              <>
                He leído y acepto{' '}
                <a href={`/legal/${tipo}`} target="_blank" rel="noreferrer">{etiqueta}</a>
              </>
            }
          />
        ))}

        <fieldset className="mt-3">
          <legend className="fs-6 fw-semibold mb-1">
            Además, acepto <span className="text-muted fw-normal">(opcional)</span>
          </legend>
          {FINALIDADES_SECUNDARIAS.map(({ campo, etiqueta }) => (
            <Form.Check key={campo} id={`registro-${campo}`} className="mb-1 small"
                        checked={form[campo]} onChange={cambiar(campo)} label={etiqueta} />
          ))}
          <Form.Text>Si no las marcas, igual puedes registrarte y usar la prueba.</Form.Text>
        </fieldset>

        <Button type="submit" className="w-100 mt-3 login-submit"
                disabled={enviando || !aceptoTodo(form) || legales.length < TIPOS_LEGALES.length}>
          {enviando ? 'Enviando…' : 'Crear mi cuenta de prueba'}
        </Button>
        <p className="small text-center mt-3 mb-0">
          ¿Ya tienes cuenta? <Link to="/login">Inicia sesión</Link>
        </p>
      </Form>
    )
  }

  return (
    <PaginaPublica titulo="Prueba Facturación gratis" subtitulo={subtitulo}
                   encabezado="Crea tu cuenta">
      {cuerpo}
    </PaginaPublica>
  )
}
